import { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  EnvelopeIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { teamAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import InviteMemberModal from '../../components/InviteMemberModal';

export default function TeamPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Base calls that all users can make
      const promises = [
        teamAPI.getMembers(),
        teamAPI.getLimits(),
      ];

      // Only admins and owners can view invitations
      const canViewInvitations = user?.role === 'owner' || user?.role === 'admin';
      if (canViewInvitations) {
        promises.push(teamAPI.getInvitations());
      }

      const results = await Promise.all(promises);

      setMembers(results[0].data.data.members || []);
      setLimits(results[1].data.data);

      if (canViewInvitations && results[2]) {
        setInvitations(results[2].data.data.invitations || []);
      } else {
        setInvitations([]);
      }

      setError(null);
    } catch (err) {
      setError('Failed to load team data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;

    try {
      setActionLoading(`remove-${memberId}`);
      await teamAPI.removeMember(memberId);
      toast.success('Team member removed successfully');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove member');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      setActionLoading(`role-${memberId}`);
      await teamAPI.updateMemberRole(memberId, newRole);
      toast.success('Role updated successfully');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    if (!confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      setActionLoading(`revoke-${invitationId}`);
      await teamAPI.revokeInvitation(invitationId);
      toast.success('Invitation revoked');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      setActionLoading(`resend-${invitationId}`);
      await teamAPI.resendInvitation(invitationId);
      toast.success('Invitation resent successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'admin':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusBadge = (status, isExpired) => {
    if (status === 'pending' && isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
          <XCircleIcon className="h-3 w-3" />
          Expired
        </span>
      );
    }

    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <ClockIcon className="h-3 w-3" />
            Pending
          </span>
        );
      case 'accepted':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircleIcon className="h-3 w-3" />
            Accepted
          </span>
        );
      case 'revoked':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400">
            <XCircleIcon className="h-3 w-3" />
            Revoked
          </span>
        );
      default:
        return null;
    }
  };

  const canManageUser = (member) => {
    if (user.role === 'owner') return member.role !== 'owner';
    if (user.role === 'admin') return member.role === 'member';
    return false;
  };

  const pendingInvitations = invitations.filter(
    (inv) => inv.status === 'pending' && !inv.isExpired
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading team..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Team Management
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your team members and invitations
          </p>
        </div>
        {(user.role === 'owner' || user.role === 'admin') && limits?.canInvite && (
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Invite Member
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Usage Stats */}
      {limits && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <UserGroupIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Team Members</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {limits.users?.current || members.length} / {limits.users?.max || '?'}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <EnvelopeIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Pending Invites</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {pendingInvitations.length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Plan</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white capitalize">
                  {limits.planName || limits.plan || 'Starter'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Notice */}
      {limits && !limits.canInvite && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-300">
                User Limit Reached
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                Your plan allows up to {limits.users?.max} users. Upgrade to add more team members.
              </p>
              <a
                href="/dashboard/plans"
                className="mt-2 inline-block text-sm font-medium text-amber-800 dark:text-amber-300 hover:underline"
              >
                View upgrade options →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Team Members */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Team Members ({members.length})
          </h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-slate-700">
          {members.map((member) => (
            <div
              key={member.id}
              className="px-4 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50"
            >
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                    {(member.name || member.email).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {member.name || 'Unnamed User'}
                    </p>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(
                        member.role
                      )}`}
                    >
                      {member.role}
                    </span>
                    {member.id === user.id && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">(You)</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{member.email}</p>
                </div>
              </div>

              {canManageUser(member) && (
                <div className="flex items-center gap-2">
                  {user.role === 'owner' && (
                    <select
                      value={member.role}
                      onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                      disabled={actionLoading === `role-${member.id}`}
                      className="text-sm border border-gray-300 dark:border-slate-600 rounded-lg px-2 py-1 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                  )}
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    disabled={actionLoading === `remove-${member.id}`}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Remove member"
                  >
                    {actionLoading === `remove-${member.id}` ? (
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    ) : (
                      <TrashIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              )}
            </div>
          ))}

          {members.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              No team members found
            </div>
          )}
        </div>
      </div>

      {/* Pending Invitations */}
      {(user.role === 'owner' || user.role === 'admin') && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Invitations ({invitations.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-slate-700">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="px-4 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50"
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <EnvelopeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {invitation.email}
                      </p>
                      {getStatusBadge(invitation.status, invitation.isExpired)}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Invited as {invitation.role} by {invitation.invitedBy?.name || 'Unknown'}
                    </p>
                  </div>
                </div>

                {invitation.status === 'pending' && !invitation.isExpired && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResendInvitation(invitation.id)}
                      disabled={actionLoading === `resend-${invitation.id}`}
                      className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                    >
                      {actionLoading === `resend-${invitation.id}` ? (
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      ) : (
                        'Resend'
                      )}
                    </button>
                    <button
                      onClick={() => handleRevokeInvitation(invitation.id)}
                      disabled={actionLoading === `revoke-${invitation.id}`}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Revoke invitation"
                    >
                      {actionLoading === `revoke-${invitation.id}` ? (
                        <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      ) : (
                        <TrashIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {invitations.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                No invitations sent yet
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={fetchData}
      />
    </div>
  );
}
